"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ScrapeReadiness } from "@/libs/tracking/scrapeReadiness";

const EMPTY_READINESS: ScrapeReadiness = {
  followers: false,
  following: false,
  lists: false,
  posts: false,
  likes: false,
  activitySummary: false,
  isCollecting: false,
};

const POLL_MS = 3000;

export interface ScrapeStatusResponse {
  pendingJobs: number;
  runningJobs: number;
  lastSuccessAt: string | null;
  isScraping: boolean;
  readiness: ScrapeReadiness;
}

function isBundleComplete(status: ScrapeStatusResponse | null | undefined): boolean {
  return Boolean(status?.readiness?.activitySummary && !status.isScraping);
}

function isActivelyScraping(status: ScrapeStatusResponse | null | undefined): boolean {
  if (!status) return false;
  return (
    status.isScraping ||
    status.readiness.isCollecting ||
    (status.pendingJobs ?? 0) > 0 ||
    (status.runningJobs ?? 0) > 0
  );
}

/**
 * Poll scrape readiness while the initial bundle is in flight.
 * Keeps polling whenever jobs are running or the bundle is incomplete.
 */
export function useScrapeReadiness(
  accountId: string | null,
  options: {
    enabled?: boolean;
    initialScrape?: ScrapeStatusResponse | null;
  } = {}
) {
  const { enabled = true, initialScrape = null } = options;
  const [status, setStatus] = useState<ScrapeStatusResponse | null>(initialScrape);
  const [loading, setLoading] = useState(
    () => Boolean(accountId && enabled && !isBundleComplete(initialScrape))
  );
  const accountIdRef = useRef(accountId);

  const fetchStatus = useCallback(async (id: string) => {
    const res = await fetch(`/api/tracking/accounts/${id}/scrape-status`);
    if (!res.ok) throw new Error("scrape status failed");
    return (await res.json()) as ScrapeStatusResponse;
  }, []);

  useEffect(() => {
    accountIdRef.current = accountId;
  }, [accountId]);

  useEffect(() => {
    if (!accountId) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(!isBundleComplete(initialScrape));
  }, [accountId, initialScrape]);

  useEffect(() => {
    if (!enabled || !accountId) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const poll = async () => {
      try {
        const next = await fetchStatus(accountId);
        if (cancelled || accountIdRef.current !== accountId) return;
        setStatus(next);
        setLoading(isActivelyScraping(next) || !next.readiness.activitySummary);
        if (isBundleComplete(next)) {
          stop();
        }
      } catch {
        if (!cancelled) setLoading(true);
      }
    };

    void poll();
    timer = setInterval(() => void poll(), POLL_MS);

    return () => {
      cancelled = true;
      stop();
    };
  }, [accountId, enabled, fetchStatus]);

  const effective = status ?? initialScrape;
  const readiness = effective?.readiness ?? EMPTY_READINESS;

  return {
    status: effective,
    readiness,
    loading,
    isScraping: isActivelyScraping(effective),
    listsReady: readiness.lists,
    activityReady: readiness.activitySummary,
    likesReady: readiness.likes,
    isCollecting: readiness.isCollecting,
  };
}
